import { Accordion, Text } from "@merge-api/merge-javascript-shared";
import { IndividualDiffResult, DiffCategoryTypes } from "../../entities";
import DiffTitle from "./DiffTitle";
import CopyButton from "./CopyButton";
import ReactDiffViewer from "react-diff-viewer-continued";
import clsx from "clsx";
import { memo, useCallback, useMemo, useState } from "react";
import { UnfoldVertical } from "lucide-react";
import { buildCopyBothText } from "../../helpers";
import useDelayedUnmount from "../../hooks/useDelayedUnmount";

// side to disable text selection on, so drags can't copy both columns as one block
type LockedSide = "left" | "right" | null;

interface IndividualViewProps {
  candidateTitle?: string;
  index: number;
  integrationID: string;
  result: IndividualDiffResult;
  collapseUnchangedLines?: boolean;
  stagedBlueprintVersionID: string;
  publishedBlueprintVersionID: string;
}

const IndividualView = ({
  candidateTitle = "Staged",
  index,
  integrationID,
  result,
  collapseUnchangedLines = false,
  stagedBlueprintVersionID,
  publishedBlueprintVersionID,
}: IndividualViewProps) => {
  // state for accordion
  const [isOpen, setIsOpen] = useState(false);
  const handleAccordionToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // hook for delaying unmounting of the accordion content for 2000ms
  const shouldRenderContent = useDelayedUnmount(isOpen, 2000);

  // never reset on mouseup: unlocking mid-release can reveal a selection that already
  // silently crossed into the other column while user-select:none hid it
  const [lockedSide, setLockedSide] = useState<LockedSide>(null);

  const handleDiffMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const cell = (event.target as HTMLElement).closest("td");
    if (!cell) return;
    // row cells are [leftGutter, leftContent, rightGutter, rightContent]
    setLockedSide(cell.cellIndex < 2 ? "left" : "right");
  }, []);

  // memoized stringify of content field values
  const stagedString = useMemo(() => JSON.stringify(result.field_values.staged_fields, null, 2), [
    result.field_values.staged_fields,
  ]);
  const publishedString = useMemo(
    () => JSON.stringify(result.field_values.published_fields, null, 2),
    [result.field_values.published_fields]
  );
  // combined, labeled string for copying both sides
  const bothString = useMemo(
    () => buildCopyBothText(publishedString, stagedString, candidateTitle),
    [publishedString, stagedString, candidateTitle]
  );
  // Define background colors based on diff category
  const getDiffBackground = () => {
    switch (result.diff_category) {
      case DiffCategoryTypes.DIFFERENT:
        return "#FFF9E6"; // Yellow background
      case DiffCategoryTypes.NET_NEW:
        return "#ebfcfa"; // Teal background
      case DiffCategoryTypes.NET_MISSING:
        return "#fff2f3"; // Red background
      default:
        return "#FCFDFF"; // Default background
    }
  };

  const diffBackground = getDiffBackground();

  const customStyles = {
    contentText: {
      fontFamily: "inherit",
      fontColor: "inherit",
      color: "inherit",
    },
    titleBlock: {
      background: "#FCFDFF",
      fontFamily: "inherit",
      fontColor: "inherit",
      color: "inherit",
      fontSize: "inherit",
      padding: 0,
      margin: 0,
    },
    lineNumber: {
      width: "25px",
      color: "#70767F !important",
    },
    // prevents the default "Expand N lines ..." row from wrapping onto multiple lines
    codeFold: {
      height: "auto",
      a: {
        textDecoration: "none !important",
        display: "inline-flex",
        alignItems: "center",
      },
      td: {
        padding: "10px 12px",
      },
    },
    variables: {
      light: {
        diffViewerBackground: "#FCFDFF",
        emptyLineBackground: "#FCFDFF",
        gutterBackground: "#FCFDFF",
        removedGutterBackground: diffBackground,
        addedGutterBackground: diffBackground,
        addedBackground: diffBackground,
        removedBackground: diffBackground,
        wordAddedBackground: diffBackground,
        wordRemovedBackground: diffBackground,
      },
    },
  };

  const titleClassNames = clsx("p-4", {
    "bg-yellow-0": result.diff_category === DiffCategoryTypes.DIFFERENT,
    "bg-teal-0": result.diff_category === DiffCategoryTypes.NET_NEW,
    "bg-red-0": result.diff_category === DiffCategoryTypes.NET_MISSING,
  });

  return (
    <Accordion
      variant="outline"
      onChange={handleAccordionToggle}
      titleClassName={titleClassNames}
      title={
        <Text as="div" variant="h6">
          Common Model {index + 1}
        </Text>
      }
    >
      {shouldRenderContent && (
        <>
          <hr className="text-gray-40 h-[0.5px] my-0" />
          <div className="flex flex-row justify-end p-2">
            <CopyButton label="Copy both" text={bothString} />
          </div>
          <div
            onMouseDown={handleDiffMouseDown}
            className={clsx("max-w-full overflow-x-auto", {
              "[&_tr>td:nth-child(3)]:select-none [&_tr>td:nth-child(4)]:select-none":
                lockedSide === "left",
              "[&_tr>td:nth-child(1)]:select-none [&_tr>td:nth-child(2)]:select-none":
                lockedSide === "right",
            })}
          >
            <ReactDiffViewer
              styles={customStyles}
              leftTitle={
                <DiffTitle
                  title="Published"
                  blueprintVersionID={publishedBlueprintVersionID}
                  integrationID={integrationID}
                  copyText={publishedString}
                  copyLabel="Copy Published"
                />
              }
              rightTitle={
                <DiffTitle
                  title={candidateTitle}
                  blueprintVersionID={stagedBlueprintVersionID}
                  integrationID={integrationID}
                  copyText={stagedString}
                  copyLabel={`Copy ${candidateTitle}`}
                />
              }
              oldValue={publishedString}
              newValue={stagedString}
              hideMarkers={true}
              showDiffOnly={collapseUnchangedLines}
              splitView={true}
              disableWordDiff={true}
              renderContent={(source) => <Text variant="md-mono">{source}</Text>}
              codeFoldMessageRenderer={(totalFoldedLines) => (
                <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap text-gray-40">
                  <UnfoldVertical size={12} />
                  <Text as="span" variant="sm">
                    Expand {totalFoldedLines} unchanged {totalFoldedLines === 1 ? "line" : "lines"}
                  </Text>
                </span>
              )}
            />
          </div>
        </>
      )}
    </Accordion>
  );
};

export default memo(IndividualView);
